import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Sidebar from './components/Sidebar';
import HeaderFilters from './components/HeaderFilters';
import DashboardCards from './components/DashboardCards';
import ParkingOwnerList from './components/ParkingOwnerList';
import ParkingOwnersList from './components/ParkingOwnersList';
import ParkingUserList from './components/ParkingUserList';
import ManualUserList from './components/ManualUserList';
import ManualBusinessList from './components/ManualBusinessList';
import OwnerDetail from './components/OwnerDetail';
import UserDetail from './components/UserDetail';
import LoginPage from './components/LoginPage';
import { dashboardService, getPendingReviewsCount, invoiceService } from './firebase/services';
import PendingReviews from './components/PendingReviews';
import InvoicesList from './components/InvoicesList';
import InvoiceDetail from './components/InvoiceDetail';
import PartnersList from './components/PartnersList';
import PartnerDetail from './components/PartnerDetail';
import BulkDeleteAuthUsers from './components/BulkDeleteAuthUsers';
import FieldDayPlanner from './components/FieldDayPlanner';
import MealDeskCompaniesManager from './components/MealDeskCompaniesManager';
import MealDeskVendorsManager from './components/MealDeskVendorsManager';
import WeeklyScheduleViewer from './components/WeeklyScheduleViewer';
import MenuEditor from './components/MenuEditor';
import ReservationsList from './components/ReservationsList';
import ReservationDetail from './components/ReservationDetail';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import UserAnalytics from './components/UserAnalytics';
import AnalyticsHub from './components/AnalyticsHub';
import RevenueAnalytics from './components/RevenueAnalytics';
import SalesPersonAnalytics from './components/SalesPersonAnalytics';
import SpaceDetail from './components/SpaceDetail';
import SpacesList from './components/SpacesList';
import ReservationAnalytics from './components/ReservationAnalytics';
import TransactionList from './components/TransactionList';

// Pass Management Components
import MultiUsePassesList from './components/MultiUsePassesList';
import OneTimePassesList from './components/OneTimePassesList';
import PartnerDealCouponsList from './components/PartnerDealCouponsList';
import PerksDealCouponsList from './components/PerksDealCouponsList';
import MultiUsePassDetail from './components/MultiUsePassDetail';
import RoleManagement from './components/RoleManagement';
import ProtectedRoute from './components/ProtectedRoute';
import ChangePassword from './components/ChangePassword';

function App() {
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    phoneNumber: '',
    fromDate: '',
    toDate: ''
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleApplyFilters = () => {
    // This will be implemented when we add data sources
    console.log('Applying filters:', filters);
  };

  const [stats, setStats] = useState({
    parkingOwners: 0,
    parkingUsers: 0,
    reviews: 0,
    invoices: 0,
    currentReservations: 0,
    futureReservations: 0,
    pastReservations: 0,
    totalEarnings: 0,
    disabledSpaces: 0,
    freeParking: 0,
    parkingSpaces: 0,
    totalPasses: 0,
    multiUsePasses: 0,
    oneTimePasses: 0,
    dealVouchers: 0,
    dealCoupons: 0,
    partners: 0,
    dealProviders: 0,
    dealSettlements: 0,
    mealVendors: 0,
    mealCustomers: 0,
    mealSettlements: 0,
    totalReservations: 0,
    confirmedReservations: 0,
    cancelledReservations: 0
  });

  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);
  const [unpaidInvoicesCount, setUnpaidInvoicesCount] = useState(0);
  const [unpaidInvoicesAmount, setUnpaidInvoicesAmount] = useState(0);

  // Authentication state listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      
      // Store user data in localStorage for permission system
      if (user) {
        const userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        };
        localStorage.setItem('currentUser', JSON.stringify(userData));
        console.log('App - User authenticated, stored in localStorage:', userData);
      } else {
        localStorage.removeItem('currentUser');
        console.log('App - User signed out, removed from localStorage');
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load dashboard stats from Firebase
  useEffect(() => {
    const loadDashboardStats = async () => {
      try {
        const dashboardStats = await dashboardService.getDashboardStats();
        setStats(dashboardStats);
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      }
    };

    if (user) {
      loadDashboardStats();
    }
  }, [user]);

  // Fetch pending reviews count and unpaid invoices data for dashboard
  useEffect(() => {
    if (user) {
      Promise.all([
        getPendingReviewsCount(),
        invoiceService.getUnpaidInvoicesData()
      ]).then(([reviewsCount, unpaidData]) => {
        console.log('Dashboard counts - Reviews:', reviewsCount, 'Unpaid Invoices:', unpaidData);
        setPendingReviewsCount(reviewsCount);
        setUnpaidInvoicesCount(unpaidData.count);
        setUnpaidInvoicesAmount(unpaidData.totalAmount);
      }).catch(error => {
        console.error('Error fetching dashboard counts:', error);
      });
    }
  }, [user]);

  // Handler to refresh dashboard numbers
  const handleDashboardRefresh = async () => {
    if (user) {
      const [dashboardStats, pendingCount, unpaidData] = await Promise.all([
        dashboardService.getDashboardStats(),
        getPendingReviewsCount(),
        invoiceService.getUnpaidInvoicesData()
      ]);
      setStats(dashboardStats);
      setPendingReviewsCount(pendingCount);
      setUnpaidInvoicesCount(unpaidData.count);
      setUnpaidInvoicesAmount(unpaidData.totalAmount);
    }
  };

  // Protected Route Component
  const ProtectedRoute = ({ children }) => {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    return children;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={
          user ? <Navigate to="/" replace /> : <LoginPage />
        } />
        
        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <HeaderFilters 
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onApplyFilters={handleApplyFilters}
                  onRefresh={handleDashboardRefresh}
                />
                <main className="flex-1 overflow-y-auto p-6">
                  <DashboardCards 
                    stats={stats} 
                    pendingReviewsCount={pendingReviewsCount}
                    unpaidInvoicesCount={unpaidInvoicesCount}
                    unpaidInvoicesAmount={unpaidInvoicesAmount}
                    onRefresh={handleDashboardRefresh}
                  />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/parking-owner-list" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ParkingOwnerList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/parking-owners-list" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ParkingOwnersList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/parking-user-list" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ParkingUserList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/spaces-list" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <SpacesList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
            
        <Route path="/manual-user-list" element={
      <ProtectedRoute>
        <div className="flex h-screen bg-gray-50">
          <Sidebar />
          <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
            <main className="flex-1 overflow-y-auto p-6">
              <ManualUserList />
            </main>
          </div>
        </div>
      </ProtectedRoute>
    } />
    
    <Route path="/manual-business-list" element={
      <ProtectedRoute>
        <div className="flex h-screen bg-gray-50">
          <Sidebar />
          <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
            <main className="flex-1 overflow-y-auto p-6">
              <ManualBusinessList />
            </main>
          </div>
        </div>
      </ProtectedRoute>
    } />
        
        <Route path="/owner-detail/:ownerId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <OwnerDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/space-detail/:spaceId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <SpaceDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/userdetail/:userId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <UserDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/pending-reviews" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <PendingReviews />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/invoices" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <InvoicesList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/invoices/:invoiceId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <InvoiceDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/partners-list" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <PartnersList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/partner-detail/:partnerId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <PartnerDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/user-management" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <BulkDeleteAuthUsers />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/field-day-planner" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50 overflow-hidden">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 min-w-0 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <FieldDayPlanner />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/kerbletmeal/customers" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MealDeskCompaniesManager />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/kerbletmeal/vendors" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MealDeskVendorsManager />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/kerbletmeal/schedule" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <WeeklyScheduleViewer />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/kerbletmeal/vendors/:vendorId/menu" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MenuEditor />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/reservations" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ReservationsList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/reservation-detail/:reservationId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ReservationDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/transactions" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <TransactionList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/analytics" element={
          <ProtectedRoute requiredPermission="quickActions.analyticsHub">
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <AnalyticsHub stats={stats} />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/reservation-analytics" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <ReservationAnalytics />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/user-analytics" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <UserAnalytics />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/revenue-analytics" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <RevenueAnalytics stats={stats} />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/sales-analytics" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <SalesPersonAnalytics stats={stats} />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Pass Management Routes */}
        <Route path="/multi-use-passes" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MultiUsePassesList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/multi-use-pass-detail/:passId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MultiUsePassDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/one-time-passes" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <OneTimePassesList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/one-time-pass-detail/:passId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MultiUsePassDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/partner-deal-coupons" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <PartnerDealCouponsList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/partner-deal-coupon-detail/:passId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MultiUsePassDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/perks-deal-vouchers" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <PerksDealCouponsList />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/perks-deal-voucher-detail/:passId" element={
          <ProtectedRoute>
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <MultiUsePassDetail />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/role-management" element={
          <ProtectedRoute requiredPermission="userManagement.users">
            <div className="flex h-screen bg-gray-50">
              <Sidebar />
              <div className="flex-1 flex flex-col ml-16 lg:ml-64 transition-all duration-300">
                <main className="flex-1 overflow-y-auto p-6">
                  <RoleManagement />
                </main>
              </div>
            </div>
          </ProtectedRoute>
        } />
        
        <Route path="/change-password" element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App; 